import store from '@state/store'
import {
  doesUserHavePermissionOfAnySet,
  QUOTES_VIEW,
  SALES_VIEW,
  PURCHASES_VIEW,
  INVOICES_VIEW,
  PROJECTS_VIEW_ANY,
  BUSINESSES_VIEW,
  BUSINESSES_EDIT,
  BRANCHES_VIEW,
  WAREHOUSES_VIEW,
  XERO_CONFIGURE,
  ROLES_VIEW,
  STAFF_VIEW,
} from '@utils/permissions'
import NProgress from 'nprogress/nprogress'
import _isEmpty from 'lodash/isEmpty'

export default [
  {
    path: '/',
    name: 'home',
    redirect: { name: 'dashboard' },
    meta: {
      authRequired: true,
    },
  },
  {
    path: '/dashboard',
    name: 'dashboard',
    meta: {
      authRequired: true,
      title: 'Dashboard',
    },
    component: () => lazyLoadView(import('@views/home.vue')),
  },
  {
    path: '/privacy-policy',
    name: 'privacy-policy',
    meta: {
      title: 'Privacy Policy',
      beforeResolve(routeTo, routeFrom, next) {
        routeTo.params.returnName = 'back';
        if (! routeFrom.path || routeFrom.path ===  '/'){
          routeTo.params.returnName = 'login';
        }
        next()
      },
    },
    component: () => import('@views/privacy-policy.vue'),
  },
  {
    path: '/login',
    name: 'login',
    component: () => lazyLoadView(import('@views/auth/login.vue')),
    meta: {
      beforeResolve(routeTo, routeFrom, next) {
        // If the user is already logged in
        if (store.getters['auth/loggedIn']) {
          // Redirect to the home page instead
          next({
            name: 'dashboard'
          })
        } else {
          // Continue to the login page
          next()
        }
      },
    },
  },
  {
    path: '/authorize',
    name: 'authorize',
    meta: {
      title: 'Login',
    },
    component: () => lazyLoadView(import('@views/auth/login.vue')),
  },
  {
    path: '/forgot-password',
    name: 'forgot-password',
    meta: {
      authRequired: false,
    },
    component: () => lazyLoadView(import('@views/auth/forgot.vue')),
  },
  {
    path: '/business-settings',
    redirect: { name: 'business-settings', params: { tab: 'detail' } },
    meta: {
      authRequired: true,
    },
  },
  {
    path: '/business-settings/:tab',
    name: 'business-settings',
    meta: {
      authRequired: true,
      title: 'Business Settings',
    },
    beforeEnter(routeTo, routeFrom, next) {
      if (validBusinessTabs.indexOf(routeTo.params.tab) === -1){
        next({ name: '404', params: { resource: 'Page' } })
      }
      if(userHasCorrectPermissionsBusiness(routeTo.params.tab)){
        next()
      }else{
        NProgress.done()
        next(false)
      }
    },
    component: () => import('@views/businesses/tabs.vue'),
    children: [
      {
        // catch create '/business-settings/:tab/create'
        // i.e: '/business-settings/terms/create'
        path: 'create',
        name: 'business-settings-create',
        component: () => import('@views/businesses/tabs.vue'),
        props: true,
        meta:{
          title: 'Business Settings',
        },
        // if we go into this route, make sure it IS populated with the correct
        // params.action as this is the `create` route
        beforeEnter(routeTo, routeFrom, next) {
          if (validBusinessMutatableTabs.indexOf(routeTo.params.tab) === -1){
            next({ name: '404', params: { resource: 'Page' } })
          }
          if (routeTo && routeTo.params && (!routeTo.params.action || routeTo.params.action !== 'create')){
            routeTo.params.action = 'create';
          }
          if(userHasCorrectPermissionsBusiness(routeTo.params.tab)){
            next()
          }else{
            NProgress.done()
            next(false)
          }
        },
      },
      {
        // catch updates '/business-settings/:tab/:id'
        // i.e: '/business-settings/terms/13'
        path: ':id',
        name: 'business-settings-update',
        component: () => import('@views/businesses/tabs.vue'),
        props: true,
        meta:{
          title: 'Business Settings',
        },
        // if we go into this route, make sure it IS populated with the correct
        // params.action as this is the `update` route
        beforeEnter(routeTo, routeFrom, next) {
          if (validBusinessMutatableTabs.indexOf(routeTo.params.tab) === -1){
            next({ name: '404', params: { resource: 'Page' } })
          }
          if (routeTo && routeTo.params && (!routeTo.params.action || routeTo.params.action !== 'update')){
            routeTo.params.action = 'update';
          }
          if(userHasCorrectPermissionsBusiness(routeTo.params.tab)){
            next()
          }else{
            NProgress.done()
            next(false)
          }
        },
      },
    ],
    props: true,
  },
  {
    path: '/xero-redirect',
    name: 'xero-redirect',
    meta: {
      authRequired: true,
      title: 'Xero Integrate',
    },
    component: () => lazyLoadView(import('@views/businesses/xero-processing.vue')),
  },
  {
    path: '/profile',
    name: 'profile',
    component: () => lazyLoadView(import('@views/profile.vue')),
    meta: {
      authRequired: true,
    },
    props: (route) => ({ user: store.state.auth.currentUser || {} }),
  },
  {
    path: '/profile/:username',
    name: 'username-profile',
    component: () => lazyLoadView(import('@views/profile.vue')),
    meta: {
      authRequired: true,
      // HACK: In order to share data between the `beforeResolve` hook
      // and the `props` function, we must create an object for temporary
      // data only used during route resolution.
      tmp: {},
      beforeResolve(routeTo, routeFrom, next) {
        store
          // Try to fetch the user's information by their username
          .dispatch('users/fetchUser', { username: routeTo.params.username })
          .then((user) => {
            // Add the user to `meta.tmp`, so that it can
            // be provided as a prop.
            routeTo.meta.tmp.user = user
            // Continue to the route.
            next()
          })
          .catch(() => {
            // If a user with the provided username could not be
            // found, redirect to the 404 page.
            next({ name: '404', params: { resource: 'User' } })
          })
      },
    },
    // Set the user from the route params, once it's set in the
    // beforeResolve route guard.
    props: (route) => ({ user: route.meta.tmp.user }),
  },
  {
    path: '/customers',
    name: 'customers',
    meta: {
      authRequired: true,
      title: 'Customer Management',
    },
    component: () => import('@views/customers/index.vue'),
    beforeEnter(routeTo, routeFrom, next) {
      routeTo.params.supportSearch = true
      next()
    },
  },
  {
    path: '/customers/create',
    name: 'create-customer',
    meta: {
      authRequired: true,
      title: 'Customer Management',
    },
    component: () => lazyLoadView(import('@views/customers/create.vue')),
  },
  {
    path: '/customers/:id',
    name: 'edit-customer',
    meta: {
      authRequired: true,
      title: 'Customer Management',
    },
    component: () => lazyLoadView(import('@views/customers/edit.vue')),
    props: true,
  },

  {
    path: '/stocks',
    name: 'stocks',
    meta: {
      authRequired: true,
      title: 'Stock Management',
    },
    component: () => import('@views/stocks/tabs.vue'),
    beforeEnter(routeTo, routeFrom, next) {
      routeTo.params.supportSearch = true
      next()
    },
    children: [
      {
        // catch tabs '/stocks/:tab'
        // i.e: '/stocks/manufactured-items'
        // i.e: '/stocks/inbounds'
        path: ':tab',
        name: 'stock-details',
        meta: {
          authRequired: true,
          title: 'Stock Management',
        },
        component: () => import('@views/stocks/tabs.vue'),
        props: true,
        beforeEnter(routeTo, routeFrom, next) {
          if (routeTo.params && routeTo.params.tab && (!isNaN(routeTo.params.tab) && !_isEmpty(routeTo.params.tab)))          {
            next({ name: 'stocks-update', params: { id: routeTo.params.tab } })
          }else if (validStockTabs.indexOf(routeTo.params.tab) === -1){
            next({ name: '404', params: { resource: 'Page' } })
          }else{
            routeTo.params.supportSearch = true
            next()
          }
        },
        children: [
          {
            // catch update '/stocks/:tab/:id'
            // i.e: '/stocks/inbounds/123'
            path: ':id',
            name: 'stocks-tab-update',
            component: () => import('@views/stocks/tabs.vue'),
            props: true,
            meta:{
              authRequired: true,
              title: 'Stock Management',
            },
            // if we go into this route, make sure it IS populated with the correct
            // params.action as this is the `update` route
            beforeEnter(routeTo, routeFrom, next) {
              if (validStockMutatableTabs.indexOf(routeTo.params.tab) === -1){
                next({ name: '404', params: { resource: 'Page' } })
              }
              if (routeTo && routeTo.params && (!routeTo.params.action || routeTo.params.action !== 'update')){
                routeTo.params.action = 'update';
              }
              routeTo.params.supportSearch = false
              next()
            },
          },
        ]
      },
      {
        // catch update '/stocks/:id'
        // i.e: '/stocks/123'
        path: ':id',
        name: 'stocks-update',
        component: () => import('@views/stocks/tabs.vue'),
        props: true,
        meta:{
          title: 'Stock Management',
        },
        // if we go into this route, make sure it IS populated with the correct
        // params.action as this is the `update` route
        beforeEnter(routeTo, routeFrom, next) {
          if (routeTo && routeTo.params && (!routeTo.params.action || routeTo.params.action !== 'update')){
            routeTo.params.action = 'update';
          }
          routeTo.params.supportSearch = false
          next()
        },
      },
    ]
  },
  {
    // TODO: This route can be removed in the future
    // keep for now for url migration period
    path: '/internal-purchase-orders/:tab',
    name: 'internal-purchase-orders',
    meta: {
      authRequired: true,
      title: 'Stock Purchases',
    },
    component: () => import('@views/internal-purchase-orders/tabs.vue'),
    props: true,
  },
  {
    path: '/stock-purchase',
    name: 'stock-purchase',
    meta: {
      authRequired: true,
      title: 'Stock Purchases',
    },
    component: () => import('@views/internal-purchase-orders/tabs.vue'),
    beforeEnter(routeTo, routeFrom, next) {
      routeTo.params.supportSearch = true
      next()
    },
    children: [
      {
        // catch create '/stock-purchase/create'
        // i.e: '/stock-purchase/create'
        path: 'create',
        name: 'stock-purchase-create',
        component: () => import('@views/internal-purchase-orders/tabs.vue'),
        meta:{
          title: 'Stock Purchases',
        },
        // if we go into this route, make sure it IS populated with the correct
        // params.action as this is the `create` route
        beforeEnter(routeTo, routeFrom, next) {
          if (routeTo && routeTo.params && (!routeTo.params.action || routeTo.params.action !== 'create')){
            routeTo.params.action = 'create';
          }
          delete routeTo.params.supportSearch
          next()
        },
      },
      {
        // catch create '/stock-purchase/:id'
        // i.e: '/stock-purchase/123'
        path: ':id',
        name: 'stock-purchase-update',
        component: () => import('@views/internal-purchase-orders/tabs.vue'),
        props: true,
        meta:{
          title: 'Stock Purchases',
        },
        // if we go into this route, make sure it IS populated with the correct
        // params.action as this is the `update` route
        beforeEnter(routeTo, routeFrom, next) {
          if (routeTo && routeTo.params && (!routeTo.params.action || routeTo.params.action !== 'update')){
            routeTo.params.action = 'update';
          }
          delete routeTo.params.supportSearch
          next()
        },
      },
    ]
  },

  {
    path: '/global-search',
    name: 'global-search',
    meta: {
      authRequired: true,
      title: 'Global Search',
    },
    component: () => lazyLoadView(import('@views/global-search/index.vue')),
    beforeEnter(routeTo, routeFrom, next) {
      routeTo.params.supportSearch = true
      next()
    },
  },
  {
    path: '/returns',
    redirect: { name: 'returns', params: { tab: 'listing' } },
    meta: {
      authRequired: true,
    },
  },
  {
    path: '/returns/:tab',
    name: 'returns',
    meta: {
      authRequired: true,
      title: 'Returns',
    },
    component: () => import('@views/returns/index.vue'),
    beforeEnter(routeTo, routeFrom, next) {
      routeTo.params.supportSearch = true
      next()
    },
    props: true,
    children: [
      {
        path: '/returns/:tab/create',
        name: 'returns-create',
        meta: {
          authRequired: true,
          title: 'Returns',
        },
        component: () => import('@views/returns/index.vue'),
        props: true,
        beforeEnter(routeTo, routeFrom, next) {
          delete routeTo.params.supportSearch
          next()
        },
      },
      {
        path: '/returns/:tab/update/:id',
        name: 'returns-update',
        meta: {
          authRequired: true,
          title: 'Returns',
        },
        component: () => import('@views/returns/index.vue'),
        props: true,
        beforeEnter(routeTo, routeFrom, next) {
          delete routeTo.params.supportSearch
          next()
        },
      },
    ]
  },
  {
    path: '/projects',
    redirect: { name: 'projects-listing' },
    meta: {
      authRequired: true,
    },
  },
  {
    path: '/projects',
    name: 'projects-listing',
    beforeEnter(routeTo, routeFrom, next) {
      if(doesUserHavePermissionOfAnySet(store.getters['auth/getUserPermissions'], PROJECTS_VIEW_ANY, store.getters['auth/getUserType']) ){
        routeTo.params.supportSearch = true
        next()
      }else{
        NProgress.done()
        next(false)
      }
    },
    meta: {
      authRequired: true,
      title: 'Projects',
    },
    component: () => import('@views/projects/index.vue'),
    props: true,
    children: [
      {
        // catch create '/projects/create'
        // i.e: '/projects/create'
        path: 'create',
        name: 'projects-create',
        component: () => import('@views/projects/index.vue'),
        props: true,
        meta: {
          authRequired: true,
          title: 'New Project',
        },
        beforeEnter(routeTo, routeFrom, next) {
          if(doesUserHavePermissionOfAnySet(store.getters['auth/getUserPermissions'], PROJECTS_VIEW_ANY, store.getters['auth/getUserType']) ){
            routeTo.params.supportSearch = false
            routeTo.params.action = 'create';
            next()
          }else{
            NProgress.done()
            next(false)
          }
        },
      },
    ],
  },
  {
    path: '/projects/:id/:tab',
    name: 'edit-project',
    props: true,
    meta: {
      authRequired: true,
      title: 'Project Details',
    },
    component: () => import('@views/projects/tabs.vue'),
    beforeEnter(routeTo, routeFrom, next) {
      if (validProjectsTabs.indexOf(routeTo.params.tab) === -1){
        next({ name: '404', params: { resource: 'Page' } })
      }
      if(userHasCorrectPermissionsProjects(routeTo.params.tab)){
        if (routeTo.params && routeTo.params.tab && ['dashboard', 'edit'].indexOf(routeTo.params.tab) !== -1){
          routeTo.params.supportSearch = false
        }else{
          routeTo.params.supportSearch = true
        }
        next()
      }else{
        NProgress.done()
        next(false)
      }
    },
    children: [
      {
        // catch create '/projects/:projectId/:tab/create'
        // i.e: '/projects/123/quotes/create'
        path: 'create',
        name: 'projects-tabs-create',
        component: () => import('@views/projects/tabs.vue'),
        props: true,
        meta: {
          authRequired: true,
          title: 'Project Details',
        },
        beforeEnter(routeTo, routeFrom, next) {
          if (validProjectsCreateTabs.indexOf(routeTo.params.tab) === -1){
            next({ name: '404', params: { resource: 'Page' } })
          }
          if (routeTo && routeTo.params && (!routeTo.params.action || routeTo.params.action !== 'create')){
            routeTo.params.action = 'create';
          }
          if(doesUserHavePermissionOfAnySet(store.getters['auth/getUserPermissions'], PROJECTS_VIEW_ANY, store.getters['auth/getUserType']) ){
            routeTo.params.supportSearch = false
            next()
          }else{
            NProgress.done()
            next(false)
          }
        },
      },
      {
        // catch update '/projects/:id/:tab/:tabId'
        // i.e: '/projects/123/quotes/456'
        path: ':tabId',
        name: 'projects-tabs-update',
        component: () => import('@views/projects/tabs.vue'),
        props: true,
        meta: {
          authRequired: true,
          title: 'Project Details',
        },
        beforeEnter(routeTo, routeFrom, next) {
          if (validProjectsMutatableTabs.indexOf(routeTo.params.tab) === -1){
            next({ name: '404', params: { resource: 'Page' } })
          }
          if (routeTo && routeTo.params && (!routeTo.params.action || routeTo.params.action !== 'update')){
            routeTo.params.action = 'update';
          }
          if(doesUserHavePermissionOfAnySet(store.getters['auth/getUserPermissions'], PROJECTS_VIEW_ANY, store.getters['auth/getUserType']) ){
            routeTo.params.supportSearch = false
            next()
          }else{
            NProgress.done()
            next(false)
          }
        },
      },
    ],
  },
  {
    path: '/suppliers',
    name: 'suppliers',
    meta: {
      authRequired: true,
      title: 'Suppliers',
    },
    component: () => lazyLoadView(import('@views/suppliers/listing.vue')),
    beforeEnter(routeTo, routeFrom, next) {
      routeTo.params.supportSearch = true
      next()
    },
  },
  {
    path: '/suppliers/create',
    name: 'create-supplier',
    meta: {
      authRequired: true,
      title: 'Create supplier',
    },
    component: () => lazyLoadView(import('@views/suppliers/create.vue')),
  },
  {
    path: '/suppliers/:id/:tab',
    name: 'supplier-tabs',
    meta: {
      authRequired: true,
      title: 'Supplier Details',
    },
    component: () => import('@views/suppliers/tabs.vue'),
    props(route) {
      const props = { ...route.params }
      props.id = +props.id
      return props
    },
    beforeEnter(routeTo, routeFrom, next) {
      if (validSupplierTabs.indexOf(routeTo.params.tab) === -1){
        next({ name: '404', params: { resource: 'Page' } })
      }
      if (routeTo && routeTo.params && routeTo.params.tab && (routeTo.params.tab !== 'dashboard' && routeTo.params.tab !== 'edit')){
        routeTo.params.supportSearch = true
      }else{
        routeTo.params.supportSearch = false
      }
      next()
    },
    children: [
      {
        // catch create '/suppliers/:id/:tab/create'
        // i.e: '/suppliers/216/manufactured-items/create'
        // i.e: '/suppliers/216/services/create'
        path: 'create',
        name: 'supplier-tabs-create',
        component: () => import('@views/suppliers/tabs.vue'),
        props(route) {
          const props = { ...route.params }
          props.id = +props.id
          props.tabId = +props.tabId
          return props
        },
        meta:{
          title: 'Supplier Details',
        },
        // if we go into this route, make sure it IS populated with the correct
        // params.action as this is the `create` route
        beforeEnter(routeTo, routeFrom, next) {
          if (validSupplierMutatableTabs.indexOf(routeTo.params.tab) === -1){
            next({ name: '404', params: { resource: 'Page' } })
          }
          if (routeTo && routeTo.params && (!routeTo.params.action || routeTo.params.action !== 'create')){
            routeTo.params.action = 'create';
          }
          routeTo.params.supportSearch = false
          next()
        },
      },
      {
        // catch EDIT '/suppliers/:id/:tab/:tabId'
        // i.e: '/suppliers/216/manufactured-items/123'
        // i.e: '/suppliers/216/services/123'
        path: ':tabId',
        name: 'supplier-tabs-update',
        component: () => import('@views/suppliers/tabs.vue'),
        props(route) {
          const props = { ...route.params }
          props.id = +props.id
          props.tabId = +props.tabId
          return props
        },
        meta:{
          title: 'Business Settings',
        },
        // if we go into this route, make sure it IS populated with the correct
        // params.action as this is the `update` route
        beforeEnter(routeTo, routeFrom, next) {
          if (validSupplierMutatableTabs.indexOf(routeTo.params.tab) === -1){
            next({ name: '404', params: { resource: 'Page' } })
          }
          if (routeTo && routeTo.params && (!routeTo.params.action || routeTo.params.action !== 'update')){
            routeTo.params.action = 'update';
          }
          routeTo.params.supportSearch = false
          next()
        },
      },
    ],
  },
  {
    path: '/finances',
    name: 'finances',
    meta: {
      authRequired: true,
      title: 'Financiers',
    },
    component: () => lazyLoadView(import('@views/finances/index.vue')),
    beforeEnter(routeTo, routeFrom, next) {
      routeTo.params.supportSearch = true
      next()
    },
  },
  {
    path: '/finances/create',
    name: 'create-finance',
    meta: {
      authRequired: true,
      title: 'Financiers',
    },
    component: () => lazyLoadView(import('@views/finances/create.vue')),
  },
  {
    path: '/finances/:id/:tab',
    name: 'finance-tabs',
    meta: {
      authRequired: true,
      title: 'Financiers',
    },
    component: () => import('@views/finances/tabs.vue'),
    beforeEnter(routeTo, routeFrom, next) {
      if (validFinancesTabs.indexOf(routeTo.params.tab) === -1){
        next({ name: '404', params: { resource: 'Page' } })
      }
      if (routeTo && routeTo.params && (!routeTo.params.id || !(!isNaN(routeTo.params.id) && !_isEmpty(routeTo.params.id)) ) ){
        next({ name: 'finances' })
      }
      if (routeTo && routeTo.params && (!routeTo.params.action || routeTo.params.action !== 'update')){
        routeTo.params.action = 'update';
      }
      next()
    },
    props: true,
  },
  {
    path: '/logout',
    name: 'logout',
    meta: {
      authRequired: false,
      beforeResolve(routeTo, routeFrom, next) {
        store.dispatch('auth/logOut')
        // const authRequiredOnPreviousRoute = routeFrom.matched.some(
        //   (route) => route.meta.authRequired
        // )
        // Navigate back to previous page, or home as a fallback
        // next(authRequiredOnPreviousRoute ? { name: 'dashboard' } : { ...routeFrom })
        next({
          name: 'login'
        })
      },
    },
  },
  {
    path: '/404',
    name: '404',
    component: require('@views/_404.vue').default,
    // Allows props to be passed to the 404 page through route
    // params, such as `resource` to define what wasn't found.
    props: true,
  },
  // Redirect any unmatched routes to the 404 page. This may
  // require some server configuration to work in production:
  // https://router.vuejs.org/en/essentials/history-mode.html#example-server-configurations
  {
    path: '*',
    redirect: '404',
  },
]

// Lazy-loads view components, but with better UX. A loading view
// will be used if the component takes a while to load, falling
// back to a timeout view in case the page fails to load. You can
// use this component to lazy-load a route with:
//
// component: () => lazyLoadView(import('@views/my-view'))
//
// NOTE: Components loaded with this strategy DO NOT have access
// to in-component guards, such as beforeRouteEnter,
// beforeRouteUpdate, and beforeRouteLeave. You must either use
// route-level guards instead or lazy-load the component directly:
//
// component: () => import('@views/my-view')
//
function lazyLoadView(AsyncView) {
  const AsyncHandler = () => ({
    component: AsyncView,
    // A component to use while the component is loading.
    loading: require('@views/_loading.vue').default,
    // Delay before showing the loading component.
    // Default: 200 (milliseconds).
    delay: 400,
    // A fallback component in case the timeout is exceeded
    // when loading the component.
    error: require('@views/_timeout.vue').default,
    // Time before giving up trying to load the component.
    // Default: Infinity (milliseconds).
    timeout: 10000,
  })

  return Promise.resolve({
    functional: true,
    render(h, { data, children }) {
      // Transparently pass any props or children
      // to the view component.
      return h(AsyncHandler, data, children)
    },
  })
}

const validProjectsCreateTabs = ['quotes']
const validProjectsMutatableTabs = validProjectsCreateTabs.concat(['sale-orders', 'purchase-orders', 'invoices'])
const validProjectsTabs = validProjectsMutatableTabs.concat(['edit', 'dashboard']);

const validBusinessMutatableTabs = ['branches', 'warehouses', 'staff', 'roles', 'terms', 'address-types']
const validBusinessTabs = validBusinessMutatableTabs.concat(['dashboard', 'detail', 'xero']);

const validSupplierMutatableTabs = ['products', 'services', 'manufactured-components', 'manufactured-items']
const validSupplierTabs = validSupplierMutatableTabs.concat(['dashboard', 'edit']);

const validFinancesTabs = ['dashboard', 'edit']

const validStockMutatableTabs = ['transfers', 'inbounds', 'outbounds']
const validStockTabs = validStockMutatableTabs.concat(['manufactured-items']);


function userHasCorrectPermissionsProjects(tab){
  let reqPermisions = []
  if (tab){
    switch (tab){
      case ('quotes'):
        reqPermisions.push(QUOTES_VIEW)
        break;
      case ('sale-orders'):
        reqPermisions.push(SALES_VIEW)
        break;
      case ('purchase-orders'):
        reqPermisions.push(PURCHASES_VIEW)
        break;
      case ('invoices'):
        reqPermisions.push(INVOICES_VIEW)
        break;
      default:
        reqPermisions = PROJECTS_VIEW_ANY
        break
    }
  }
  return doesUserHavePermissionOfAnySet(store.getters['auth/getUserPermissions'], reqPermisions, store.getters['auth/getUserType'])
}

function userHasCorrectPermissionsBusiness(tab){
  const reqPermisions = []
  if (tab){
    switch (tab){
      case ('dashboard'):
        reqPermisions.push(BUSINESSES_VIEW)
        break;
      case ('detail'):
        reqPermisions.push(BUSINESSES_EDIT)
        break;
      case ('branches'):
        reqPermisions.push(BRANCHES_VIEW)
        break;
      case ('warehouses'):
        reqPermisions.push(WAREHOUSES_VIEW)
        break;
      case ('staff'):
        reqPermisions.push(STAFF_VIEW)
        break;
      case ('roles'):
        reqPermisions.push(ROLES_VIEW)
        break;
      case ('xero'):
        reqPermisions.push(XERO_CONFIGURE)
        break;
      case ('terms'):
        reqPermisions.push(BUSINESSES_VIEW)
        break;
      case ('address-types'):
        reqPermisions.push(BUSINESSES_VIEW)
        break;
      default:
        reqPermisions.push(BUSINESSES_VIEW)
        break
    }
  }
  return doesUserHavePermissionOfAnySet(store.getters['auth/getUserPermissions'], reqPermisions, store.getters['auth/getUserType'])
}
